/**
 * Signed-URL fetch for one PDF, shared by every in-app viewer.
 *
 * Extracted from PdfPreviewModal on 2026-07-29 when the side-by-side compare
 * viewer needed the same thing twice. Two copies of "mint a URL, race-guard the
 * response, surface the failure" is exactly the kind of duplication where one
 * copy quietly stops handling an error the other one does.
 *
 * The URL is minted on every open (15-min TTL is cheap to refresh) and never
 * cached between opens — a stale URL is worse UX than one extra round-trip.
 * Pass `null` to hold the hook inert; that's the closed state.
 */
import { useEffect, useState } from 'react';
import { api } from './api';
import { useAuth } from './AuthContext';

export interface PdfSignedUrl {
  url: string | null;
  /** Server-derived display filename; the fallback only shows pre-fetch. */
  filename: string;
  error: string | null;
}

/**
 * Which document to mint a URL for.
 *
 * A bare string is a `pdfs`-table id — the foreman's daily T&M report, and the
 * only kind of document there was until subcontractor lines started carrying
 * vendor invoices (ADR 0009). Those live under the rollup's routes with no
 * `pdfs` row of their own, so they can't be named by an id alone.
 *
 * A `generated` source is a REGENERATED copy of a daily — the reprinted page a
 * send attached instead of the original (`generated_documents`). It is tagged
 * rather than a bare id because its id names a different table: an untagged id
 * would silently resolve against `daily_pdfs` and 404.
 *
 * The union stays here rather than at the call sites because this module is the
 * one place that knows how to turn any of them into a URL.
 */
export type PdfSource =
  | string
  | { kind: 'subcontractor-invoice'; rollupId: string; lineId: string }
  | { kind: 'generated'; id: string };

/** Shown in the viewer header until the server's own filename arrives. */
const DEFAULT_FILENAME = 'Document.pdf';

/**
 * Stable identity of a source, for use as an effect dependency. Two sources
 * with the same key name the same document, so they are interchangeable.
 * `null` means "nothing to fetch" — an empty id is nothing to fetch, in either
 * branch: a half-built object (an id still loading) must not mint a URL for
 * `sub-invoice::` and surface the 404 as if the document were missing.
 *
 * The two branches are prefixed so they can never collide.
 */
export function pdfSourceKey(source: PdfSource | null): string | null {
  if (source === null) return null;
  if (typeof source === 'string') return source === '' ? null : `pdf:${source}`;
  if (source.kind === 'generated') return source.id === '' ? null : `generated:${source.id}`;
  if (source.rollupId === '' || source.lineId === '') return null;
  return `sub-invoice:${source.rollupId}:${source.lineId}`;
}

function mintPdfUrl(token: string, source: PdfSource) {
  if (typeof source === 'string') return api.pdfs.signedUrl(token, source);
  if (source.kind === 'generated') return api.pdfs.generatedSignedUrl(token, source.id);
  return api.rollups.subcontractorInvoiceUrl(token, source.rollupId, source.lineId);
}

/**
 * Chrome's built-in PDF viewer reads these from the URL FRAGMENT: collapse the
 * thumbnail sidebar and fit the page to the pane's width. In a side-by-side
 * pane the sidebar eats most of the usable width, so a zoomed page has nowhere
 * to go. The reader can still reopen thumbnails from the viewer's own menu.
 *
 * A fragment is never sent to the server, so this cannot invalidate a presigned
 * URL's signature — and it's appended at render time, so it never re-mints.
 */
export function withPdfViewerParams(url: string | null): string | null {
  if (!url) return url;
  // Defensive: a fragment already on the URL would make a second '#' literal.
  const base = url.split('#')[0] ?? url;
  return `${base}#navpanes=0&view=FitH`;
}

export function usePdfSignedUrl(source: PdfSource | null): PdfSignedUrl {
  const { token } = useAuth();
  const [url, setUrl] = useState<string | null>(null);
  const [filename, setFilename] = useState<string>(DEFAULT_FILENAME);
  const [error, setError] = useState<string | null>(null);
  const key = pdfSourceKey(source);

  useEffect(() => {
    if (!source || !key || !token) {
      setUrl(null);
      setFilename(DEFAULT_FILENAME);
      setError(null);
      return;
    }
    setUrl(null);
    // The filename resets with the URL: it is the PREVIOUS document's name until
    // this fetch answers, and leaving it up means a failed mint shows the header
    // of whatever was open last — now potentially a daily PDF's name over an
    // invoice's error.
    setFilename(DEFAULT_FILENAME);
    setError(null);
    let cancelled = false;
    void (async () => {
      try {
        const res = await mintPdfUrl(token, source);
        if (cancelled) return;
        setUrl(res.url);
        setFilename(res.filename);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load PDF');
      }
    })();
    return () => {
      cancelled = true;
    };
    // Keyed on `key`, not `source`: an object source is a fresh literal on
    // every render of whoever built it, and depending on its identity would
    // re-mint in a loop. The captured source always matches the key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, token]);

  return { url, filename, error };
}
